import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * ConfirmModal - Reusable confirmation dialog
 *
 * Features:
 * - Accessible modal with focus trap
 * - Escape key to close
 * - Click outside to close
 * - Customizable title, message, and buttons
 * - Danger variant for destructive actions
 * - Optional extraBody slot for caller-rendered controls (e.g. reason textarea)
 * - Optional typedConfirmation: requires the user to retype a value before
 *   the Confirm button enables (Phase 4 — used for trusted-domain adds where
 *   blast radius is huge; mirrors `git branch -D` / Stripe live-mode UX)
 * - Optional confirmDisabled flag for callers that need to gate Confirm on
 *   external state (e.g. non-empty reason textarea)
 */
function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default', // 'default' | 'danger'
  loading = false,
  extraBody = null,
  typedConfirmation = null, // when set (string), require user to retype it
  confirmDisabled = false,
}) {
  // Typed-confirmation input value, local to this modal instance. Resets
  // whenever the modal opens so reopening a previously-typed value doesn't
  // leak across sessions.
  const [typedValue, setTypedValue] = useState('');
  useEffect(() => {
    if (isOpen) setTypedValue('');
  }, [isOpen]);
  const modalRef = useRef(null);
  const confirmButtonRef = useRef(null);

  // Focus confirm button when modal opens — UNLESS typedConfirmation is set,
  // in which case the user must interact with the input first.
  useEffect(() => {
    if (isOpen && confirmButtonRef.current && !typedConfirmation) {
      confirmButtonRef.current.focus();
    }
  }, [isOpen, typedConfirmation]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle click outside
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const confirmButtonClasses = variant === 'danger'
    ? 'bg-red-600 hover:bg-red-700 text-white'
    : 'bg-decisiv-blue hover:bg-decisiv-blue/90 text-white';

  // Portal to <body>: a `fixed` overlay is positioned against the nearest
  // transformed ancestor, and the sidebar is one (`gpu-accelerated`), so
  // rendered in place the dialog was trapped inside the sidebar and its
  // backdrop blocked it.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 modal-backdrop-transition animate-fadeIn"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        ref={modalRef}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6
          animate-fadeInScale gpu-accelerated"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-4">
          {/* Icon */}
          {variant === 'danger' ? (
            <div className="shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30
              flex items-center justify-center">
              <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          ) : (
            <div className="shrink-0 w-10 h-10 rounded-full bg-decisiv-blue/10
              flex items-center justify-center">
              <svg className="w-5 h-5 text-decisiv-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          )}

          <div className="flex-1">
            <h3
              id="modal-title"
              className="text-lg font-semibold text-gray-900 dark:text-white"
            >
              {title}
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {message}
            </p>
            {extraBody && <div className="mt-4">{extraBody}</div>}
            {typedConfirmation && (
              <div className="mt-4">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-decisiv-blue font-mono">{typedConfirmation}</code> to confirm
                </label>
                <input
                  type="text"
                  value={typedValue}
                  onChange={(e) => setTypedValue(e.target.value)}
                  autoFocus
                  autoComplete="off"
                  spellCheck="false"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600
                    rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                    focus:outline-hidden focus:ring-2 focus:ring-decisiv-blue font-mono"
                />
              </div>
            )}
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="shrink-0 p-1 text-gray-400 hover:text-gray-600
              dark:hover:text-gray-200 transition-colors rounded-lg
              hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium
              text-gray-700 dark:text-gray-300
              bg-gray-100 dark:bg-gray-700
              hover:bg-gray-200 dark:hover:bg-gray-600
              rounded-lg transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cancelText}
          </button>
          <button
            ref={confirmButtonRef}
            onClick={onConfirm}
            disabled={
              loading ||
              confirmDisabled ||
              (typedConfirmation != null && typedValue !== typedConfirmation)
            }
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed
              flex items-center gap-2 ${confirmButtonClasses}`}
          >
            {loading && (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            {confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default ConfirmModal;
